import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getOnboardingChecklist, logTourEvent } from 'api/onboarding';
import { TourChecklistItem, LogTourEventPayload } from 'api/onboarding/types';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';

interface OnboardingContextValue {
	/** All active tours with the current user's latest status. */
	tours: TourChecklistItem[];
	/** True while the checklist is being (re)fetched. */
	loading: boolean;
	/** True if the checklist fetch failed — UI should degrade gracefully. */
	error: boolean;
	/** Tour keys the user has already completed (convenience derived set). */
	completedTourKeys: Set<string>;
	/** Re-fetch the checklist from the backend. */
	refresh: () => Promise<void>;
	/** Log a walkthrough event, then refresh so status reflects it. */
	logEvent: (payload: LogTourEventPayload) => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
	undefined,
);

// App-level provider for the onboarding/walkthrough checklist. Resilient by
// design: the fetch is fully try/caught and never throws, so a backend failure
// surfaces as `error` (UI degrades) and can never crash the host app.
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [tours, setTours] = useState<TourChecklistItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);

	// selectUserInfo returns the slice; the user data is nested under it. Empty
	// (''/undefined) until the session is resolved.
	const userId = useSelector(selectUserInfo)?.userInfo?.userId;

	const refresh = useCallback(async () => {
		setLoading(true);
		setError(false);
		try {
			const data = await getOnboardingChecklist();
			setTours(data);
		} catch {
			setError(true);
			setTours([]);
		} finally {
			setLoading(false);
		}
	}, []);

	// Fetch only once the user session is resolved. The provider mounts at app
	// root — on a cold load that's before auth/user info exists, so fetching then
	// resolves no user and returns nothing, with no retry (the checklist then only
	// appeared after a manual refresh). Re-runs if the user changes.
	useEffect(() => {
		if (userId) refresh();
	}, [userId, refresh]);

	const logEvent = useCallback(
		async (payload: LogTourEventPayload) => {
			const ok = await logTourEvent(payload);
			if (ok) {
				await refresh();
			}
		},
		[refresh],
	);

	const completedTourKeys = useMemo(
		() =>
			new Set(
				tours
					.filter((t) => t.status === 'completed')
					.map((t) => t.tourKey),
			),
		[tours],
	);

	const value = useMemo<OnboardingContextValue>(
		() => ({ tours, loading, error, completedTourKeys, refresh, logEvent }),
		[tours, loading, error, completedTourKeys, refresh, logEvent],
	);

	return (
		<OnboardingContext.Provider value={value}>
			{children}
		</OnboardingContext.Provider>
	);
};

export const useOnboarding = (): OnboardingContextValue => {
	const ctx = useContext(OnboardingContext);
	if (!ctx) {
		throw new Error('useOnboarding must be used within an OnboardingProvider');
	}
	return ctx;
};
